import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sparkline — small inline SVG line chart for scorecard tiles, dossier
 * summaries, and KPI rails. Mirror of the prototype's `Sparkline` in
 * app-ui.jsx — same path math, same tail dot.
 *
 * Edge cases:
 *   - 0 or 1 data points → renders a dash so the layout doesn't jump
 *   - flat series (all values equal) → renders a horizontal mid-line
 */
export function Sparkline({ values, color = 'var(--color-ink-secondary)', width = 80, height = 22, strokeWidth = 1.4, className, }) {
    if (values.length < 2) {
        return (_jsx("svg", { width: width, height: height, className: className, "aria-hidden": true, children: _jsx("line", { x1: "0", y1: height / 2, x2: width, y2: height / 2, stroke: color, strokeWidth: strokeWidth, strokeLinecap: "round", opacity: 0.4 }) }));
    }
    const max = Math.max(...values);
    const min = Math.min(...values);
    // Flat series: every point would land at `height - 0 = height`
    // (the bottom edge) because (v-min)/span is 0 when min === max.
    // Mirror the `< 2` early-return treatment and pin to `height / 2`.
    const flat = max === min;
    const span = flat ? 1 : max - min;
    const step = width / (values.length - 1);
    const yFor = (v) => (flat ? height / 2 : height - ((v - min) / span) * height);
    const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${yFor(v)}`).join(' ');
    // values.length >= 2 from the early return above, so the last index
    // is always a valid number — but TS strict can't infer that, hence
    // the non-null assertion on the indexed read.
    const lastValue = values[values.length - 1];
    const tailX = (values.length - 1) * step;
    const tailY = yFor(lastValue);
    return (_jsxs("svg", { width: width, height: height, style: { overflow: 'visible' }, className: className, "aria-hidden": true, children: [_jsx("path", { d: path, stroke: color, strokeWidth: strokeWidth, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: tailX, cy: tailY, r: 2.4, fill: color })] }));
}
